import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../contexts/AppContext';
import { RegisterUserRequest } from '../types';
import { apiService } from '../services/apiService';
import { User, Phone, Car, Loader2, AlertTriangle, MessageCircle, X } from 'lucide-react';
import { 
  formatLicensePlate, 
  formatPhoneNumber, 
  getCleanPhoneNumber, 
  isValidLicensePlate,
  isValidPhoneNumber 
} from '../utils/formatters';
import { mapApiErrorToMessage } from '../utils/errorHandling';

export function RegistrationPage() {
  const { t } = useTranslation();
  const { login, setError } = useApp();
  const [formData, setFormData] = useState<RegisterUserRequest>({
    initData: '',
    fullName: '',
    phoneNumber: '',
    primaryPlate: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [registrationError, setRegistrationError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Clear error when user starts typing
    if (registrationError) {
      setRegistrationError(null);
      setShowErrorModal(false);
    }
    
    if (name === 'primaryPlate') {
      // Auto-format license plate as user types
      const formatted = formatLicensePlate(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else if (name === 'phoneNumber') {
      // Auto-format phone number as user types
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // Get Telegram WebApp data
      const tg = window.Telegram?.WebApp;
      if (!tg || !tg.initData) {
        throw new Error(t('errors.unauthorized'));
      }

      // Validate form
      if (!formData.fullName.trim()) {
        throw new Error(t('errors.validation'));
      }
      if (!formData.phoneNumber.trim()) {
        throw new Error(t('errors.validation'));
      }
      if (!formData.primaryPlate.trim()) {
        throw new Error(t('errors.validation'));
      }
      
      // Validate phone number format
      if (!isValidPhoneNumber(formData.phoneNumber)) {
        throw new Error('Phone number must be in valid international format (e.g., +1 555 123-4567)');
      }
      
      // Validate license plate format
      if (!isValidLicensePlate(formData.primaryPlate)) {
        throw new Error('License plate must be in format nnn-nn-nnn (e.g., 123-45-678)');
      }

      // Submit registration with Telegram init data and clean values
      const registrationData = {
        initData: tg.initData,
        fullName: formData.fullName.trim(),
        phoneNumber: getCleanPhoneNumber(formData.phoneNumber), // Send clean E.164 format
        primaryPlate: formData.primaryPlate // Already in correct format
      };
      await apiService.post('/user/register', registrationData);

      // Try to login after registration
      await login();
    } catch (error) {
      console.error('Registration error:', error);
      
      // Map the error to a user-friendly message
      const errorKey = mapApiErrorToMessage(error);
      setRegistrationError(errorKey);
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTryAgain = () => {
    setRegistrationError(null);
    setShowErrorModal(false);
  };

  const handleContactSupport = () => {
    // Open external support link
    window.open('https://t.me/your_support_bot', '_blank'); // Replace with actual support bot
  };

  return (
    <div className="modern-page">
      <div className="modern-page-container">
        <div className="modern-card-primary">
          <div className="modern-page-header-simple">
            <div className="page-icon">
              <User size={32} />
            </div>
            <h1 className="page-title-large">{t('auth.welcome')}</h1>
            <p className="page-subtitle-large">
              {t('auth.registration.subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="modern-form">
            <div className="modern-form-group">
              <label htmlFor="fullName" className="modern-form-label">
                <User size={20} />
                {t('auth.registration.fullName')} *
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder={t('auth.registration.fullNamePlaceholder')}
                className="modern-form-input"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="modern-form-group">
              <label htmlFor="phoneNumber" className="modern-form-label">
                <Phone size={20} />
                {t('auth.registration.phone')} *
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder={t('auth.registration.phonePlaceholder')}
                className="modern-form-input"
                required
                disabled={isSubmitting}
              />
              <small className="modern-form-hint">Auto-formatted as you type. Include country code (e.g., +1 for US, +972 for Israel)</small>
            </div>

            <div className="modern-form-group">
              <label htmlFor="primaryPlate" className="modern-form-label">
                <Car size={20} />
                {t('auth.registration.licensePlate')} *
              </label>
              <input
                type="text"
                id="primaryPlate"
                name="primaryPlate"
                value={formData.primaryPlate}
                onChange={handleInputChange}
                placeholder={t('auth.registration.licensePlatePlaceholder')}
                pattern="\d{3}-\d{2}-\d{3}"
                title="License plate must be in format nnn-nn-nnn (e.g., 123-45-678)"
                className="modern-form-input"
                required
                disabled={isSubmitting}
              />
              <small className="modern-form-hint">Auto-formatted as you type. Enter digits only: 12345678 → 123-45-678</small>
            </div>

            <button 
              type="submit" 
              className={`btn-modern btn-primary-modern btn-large ${isSubmitting ? 'loading' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  {t('common.loading')}
                </>
              ) : (
                t('auth.registration.submit')
              )}
            </button>
          </form>

          <div className="modern-info-box">
            <p className="info-text">
              {t('auth.pending.message')}
            </p>
          </div>
        </div>
      </div>

      {/* Error Modal */}
      {showErrorModal && registrationError && (
        <RegistrationErrorModal
          errorKey={registrationError}
          onTryAgain={handleTryAgain}
          onContactSupport={handleContactSupport}
          onClose={() => setShowErrorModal(false)}
        />
      )}
    </div>
  );
}

interface RegistrationErrorModalProps {
  errorKey: string;
  onContactSupport?: () => void;
  onTryAgain?: () => void;
  onClose: () => void;
}

function RegistrationErrorModal({ errorKey, onContactSupport, onTryAgain, onClose }: RegistrationErrorModalProps) {
  const { t } = useTranslation();
  
  const isConflictError = errorKey.includes('AlreadyRegistered');
  
  return (
    <div className="modal-overlay-modern">
      <div className="modal-modern">
        <div className="modal-header-modern">
          <h3 className="modal-title">
            <AlertTriangle size={24} className="text-error" style={{ marginRight: '8px' }} />
            {isConflictError 
              ? t('auth.registration.errors.conflictTitle', 'Registration Conflict')
              : t('errors.error', 'Error')
            }
          </h3>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <div className="modal-content-modern">
          <p className="modal-description">
            {t(errorKey)}
          </p>
          
          {isConflictError && (
            <div className="error-suggestions">
              <h4 className="suggestions-title">
                {t('auth.registration.errors.suggestions', 'What you can do:')}
              </h4>
              <ul className="suggestions-list">
                <li>{t('auth.registration.errors.suggestion1', 'Try using a different phone number or license plate')}</li>
                <li>{t('auth.registration.errors.suggestion2', 'Contact support if you believe this is an error')}</li>
                <li>{t('auth.registration.errors.suggestion3', 'Check if you already have an account')}</li>
              </ul>
            </div>
          )}
        </div>
        
        <div className="modal-actions-modern">
          {onTryAgain && (
            <button 
              onClick={onTryAgain}
              className="btn-modern btn-secondary-modern"
            >
              {t('common.tryAgain', 'Try Again')}
            </button>
          )}
          
          {onContactSupport && (
            <button 
              onClick={onContactSupport}
              className="btn-modern btn-primary-modern"
            >
              <MessageCircle size={18} />
              {t('auth.registration.contactSupport')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}